import { yieldToMainThread } from '@theme/utilities';
import { Component } from '@theme/component';

/**
 * FlyToCart custom element for animating product images to cart
 * This component creates a visual effect of a product "flying" to the cart when added
 */
class FlyToCart extends Component {
  /** @type {Element} */
  source;

  /** @type {boolean} */
  useSourceSize = false;

  /** @type {Element} */
  destination;

  connectedCallback() {
    super.connectedCallback();
    const intersectionObserver = new IntersectionObserver((entries) => {
      /** @type {DOMRectReadOnly | null} */
      let sourceRect = null;
      /** @type {DOMRectReadOnly | null} */
      let destinationRect = null;

      entries.forEach((entry) => {
        if (entry.target === this.source) {
          sourceRect = entry.boundingClientRect;
        } else if (entry.target === this.destination) {
          destinationRect = entry.boundingClientRect;
        }
      });

      if (sourceRect && destinationRect) {
        this.#animate(sourceRect, destinationRect);
      }

      intersectionObserver.disconnect();
    });
    intersectionObserver.observe(this.source);
    intersectionObserver.observe(this.destination);
  }

  /**
   * Animates the flying thingy along the bezier curve.
   * @param {DOMRectReadOnly} sourceRect - The bounding client rect of the source.
   * @param {DOMRectReadOnly} destinationRect - The bounding client rect of the destination.
   */
  #animate = async (sourceRect, destinationRect) => {
    //Define bezier curve points
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2,
      y: sourceRect.top + sourceRect.height / 2,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2,
      y: destinationRect.top + destinationRect.height / 2,
    };

    // Position the flying thingy back to the start point
    if (this.useSourceSize) {
      this.style.setProperty('--width', `${sourceRect.width}px`);
      this.style.setProperty('--height', `${sourceRect.height}px`);
    }
    this.style.setProperty('--start-x', `${startPoint.x}px`);
    this.style.setProperty('--start-y', `${startPoint.y}px`);
    const travelX = endPoint.x - startPoint.x;
    const travelY = endPoint.y - startPoint.y;

    this.style.setProperty('--travel-x', `${travelX}px`);
    this.style.setProperty('--travel-y', `${travelY}px`);

    // Derive the duration from the distance travelled. A single fixed duration is what
    // made this feel slow on a phone: the cart icon is a few hundred pixels from the
    // button there against a thousand or more on a desktop, so the same 0.52s was
    // spread across a much shorter path and the item crawled.
    //
    // Distance is only known here, and an inline custom property outranks a media
    // query, so the timing has to be set alongside the travel values rather than in CSS.
    const distance = Math.hypot(travelX, travelY);

    // Phones hold the whole gesture in central vision, so a velocity that reads as
    // brisk across a wide desktop reads as laboured on a handset. They get a higher one.
    const narrow = window.matchMedia('(max-width: 749px)').matches;
    const pixelsPerSecond = narrow ? 1900 : 1500;

    // The clamps, not the velocity, decide the common cases: anything under ~490px of
    // travel on a phone lands on the 0.26s floor, and anything over ~825px on a desktop
    // lands on the 0.55s ceiling. That is intended. Strict proportionality turns short
    // hops into a flicker and long ones into a drag, so apparent speed is deliberately
    // allowed to vary — roughly 1200px/s on a short mobile hop up to 2700px/s on a wide
    // desktop throw — while duration stays inside a range that reads as deliberate.
    const seconds = Math.min(0.55, Math.max(0.26, distance / pixelsPerSecond));

    this.style.setProperty('--fly-duration', `${seconds.toFixed(3)}s`);

    await yieldToMainThread();

    await Promise.allSettled(this.getAnimations().map((a) => a.finished));
    this.remove();
  };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
